/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable spire/export-styles */
import Constants from "@husky-hrc/Utilities/Constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface CheckoutTermsAndConditionsStyles {
    modalStyles?: ModalPresentationProps;
    modalButtonContainer?: InjectableCss;
    closeButton?: ButtonPresentationProps;
    errorMessageStyles?: TypographyPresentationProps;
    checkboxStyles?: CheckboxProps;
}

const checkoutTermsAndConditionsStyles: CheckoutTermsAndConditionsStyles = {
    modalStyles: Constants.Styles.modalStyles.modal,
    modalButtonContainer: {
        css: css`
            margin-top: 20px;
            display: flex;
            justify-content: center;
        `,
    },
    closeButton: {
        color: "primary",
        size: 50,
        typographyProps: {
            css: css`
                letter-spacing: 0.23px;
            `,
        },
        css: css`
            border-radius: 4px;
            width: 200px;
        `,
    },
    errorMessageStyles: {
        size: 12,
        weight: "normal",
        lineHeight: "22px",
        color: "danger.main",
    },
    checkboxStyles: {
        typographyProps: {
            errorColor: "text.main",
        },
    },
};

const mapStateToProps = (state: ApplicationState, widgetProps: WidgetProps) => ({
    cart: getCurrentCartState(state).value,
    language: state.context.session.language,
});

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = checkoutTermsAndConditionsStyles;

const CheckoutTermsAndConditions: React.FC<Props> = ({ cart, language }) => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const [termsAccepted, setTermsAccepted] = React.useState(false);
    const [termsViewed, setTermsViewed] = React.useState(false);
    const [tcDocument, setTCDocument] = React.useState("");
    const [error, setError] = React.useState("");
    const context = React.useContext(CheckoutShippingFormContext);

    React.useEffect(() => {
        if (cart?.properties.tcDocument) {
            setTCDocument(cart.properties.tcDocument);
        }
    }, [cart]);

    React.useEffect(() => {
        context.validators["checkoutTermsAndConditions"] = validate;
    }, [termsAccepted]);

    const validate = async () => {
        await setError(
            termsAccepted ? "" : translate("You must read and agree to the terms and conditions before proceeding."),
        );
        return termsAccepted;
    };

    const getTermsByProductAndRegion = () => {
        const tcDocumentName = tcDocument.toLowerCase();
        const lang = language ? language.languageCode : "en_US";
        return `/userfiles/termsconditions/${lang}/${tcDocumentName}.pdf`;
    };

    const handleTermsClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setModalOpen(true);
        setTermsViewed(true);
    };

    const handleCheckboxChange = (event: React.SyntheticEvent, value: boolean) => {
        setError("");
        setTermsAccepted(value);
    };

    return (
        <>
            <StyledWrapper style={{ marginBottom: 50 }}>
                <CheckboxGroup>
                    <Checkbox
                        error={!!error}
                        disabled={!termsViewed}
                        onChange={handleCheckboxChange}
                        checked={termsAccepted}
                        data-test-selector="checkout_termsAndConditions"
                        {...styles.checkboxStyles}
                    >
                        {translate("By continuing, I agree to the")}{" "}
                        <Link
                            data-test-selector="checkout_termsAndConditions_link"
                            type="button"
                            onClick={handleTermsClick}
                            color="danger.main"
                        >
                            {translate("Terms and Conditions")}*
                        </Link>
                        <Typography as="span" italic={true}>
                            {translate(
                                "Please note that you must view the Terms and Conditions before you can check this box and proceed with checkout.",
                            )}
                        </Typography>
                    </Checkbox>
                </CheckboxGroup>
                {error && (
                    <Typography role="alert" {...styles.errorMessageStyles}>
                        {error}
                    </Typography>
                )}
            </StyledWrapper>
            <Modal
                headline={translate("Terms & Conditions")}
                isOpen={modalOpen}
                handleClose={() => setModalOpen(false)}
                {...styles.modalStyles}
            >
                <object data={getTermsByProductAndRegion()} type="application/pdf" width="100%" height="480px">
                    <a href={getTermsByProductAndRegion()} target="_blank" style={{ color: "#DE2C0F" }}>
                        {translate("Open Terms and Conditions Document")}
                    </a>
                </object>
                <StyledWrapper {...styles.modalButtonContainer}>
                    <Button {...styles.closeButton} onClick={() => setModalOpen(false)}>
                        {translate("Close")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutTermsAndConditions),
    definition: {
        group: "Checkout" as any,
        displayName: "Checkout Terms & Conditions",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
